import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ConfigContext } from '../../Context/ConfigContext';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import { InputNumber } from 'primereact/inputnumber';

const StockValuationReport = () => {
    const [rows, setRows] = useState([]);
    const { apiUrl, apiHeadersJson, store_id } = useContext(ConfigContext);
    const toast = useRef(null);
    const [dataTableLoading, setDataTableLoading] = useState(true);
    const headers = apiHeadersJson;
    const [keyword, setKeyword] = useState('');
    const [callApi, setCallApi] = useState(true);
    const navigate = useNavigate();
    const [CategoryId, setCategoryId] = useState('');
    const [categories, setCategories] = useState([]);
    const { limit, order } = useParams();
    const [rowsPerPage, setRowsPerPage] = useState(limit || 10);
    const [sortOrder, setSortOrder] = useState(order, 'DESC');

    if (!limit || !order || !['ASC', 'DESC'].includes(order.toUpperCase())) {
        navigate('/StockValuation/300/DESC')
    }

    // Fetch stock report data
    const GetList = async () => {
        setDataTableLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/Reports/GetStockValuationReport`, {
                params: { store_id, keyword, category_id: CategoryId },
                headers
            });
            const { success, data, message } = response.data;
            if (success) {
                setRows(data);
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.error('Error fetching stock report:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch data', life: 3000 });
        } finally {
            setDataTableLoading(false);
            setCallApi(false);
        }
    };

    const fetchcategorys = async () => {
        try {
            const [categoryRes] = await Promise.all([
                axios.get(`${apiUrl}/Masters/GetCategoryList`, { params: { store_id: store_id }, headers }),
            ]);
            setCategories(categoryRes.data.data || []);
        } catch (error) {
            console.error('Error fetching categorys:', error);
        }
    };

    useEffect(() => {
        store_id && fetchcategorys();
        setCategoryId('')
    }, [store_id]);

    useEffect(() => {
        CategoryId && GetList();
    }, [CategoryId]);

    useEffect(() => {
        callApi && GetList();
    }, [callApi]);

    useEffect(() => {
        store_id && GetList();
    }, [store_id, order, limit]);


    // Debounce search (to avoid excessive API calls)
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (keyword !== '') {
                GetList();
            }
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [keyword]);

    // Apply filters
    const applyFilters = () => {
        setCallApi(true);
    };

    // Clear filters
    const clearFilters = () => {
        setKeyword("");
        setCallApi(true);
        setKeyword('');
        setCategoryId('');
    };

    const paginatorLeft = <Button type="button" icon="pi pi-refresh" text onClick={GetList} />;

    return (
        <div className="container-fluid">
            <Toast ref={toast} />
            <div className="d-flex justify-content-between align-items-center mt-4">
                <h4 className="fw-bold">Stock Valuation Report</h4>
                <h6 className="text-muted">Reports / Stock Valuation Report</h6>
            </div>

            <div className="card mt-4 p-3 shadow-sm">
                {/* Card Header */}
                <div className="d-flex justify-content-between align-items-center bg-dark text-white p-3 rounded-top">
                    <h5>Stock Valuation Report</h5>
                </div>

                {/* Filters */}
                <div className='card-header py-3'>
                    <div className="row">
                        <div className="col-md-3">
                            <IconField iconPosition="left" className='w-100'>
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={keyword}
                                    onChange={(e) => setKeyword(e.target.value)}
                                    className='w-100'
                                    placeholder="Search by product..."
                                />
                            </IconField>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select h-100"
                                value={CategoryId || ""}
                                required
                                onChange={(e) => setCategoryId(e.target.value)}>

                                <option value="" disabled>
                                    Select a Category
                                </option>
                                {categories.map((category) => (
                                    <option key={category.category_id} value={category.category_id}>
                                        {category.category_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <div className="d-flex">
                                <Button label="Filter" icon="pi pi-filter" className="ms-3" onClick={applyFilters} disabled={!keyword && !store_id} />
                                <Button label="Clear" icon="pi pi-times" severity='secondary' className="ms-3" onClick={clearFilters} disabled={!keyword && !store_id} />

                            </div>
                        </div>
                    </div>

                </div>

                {/* Table */}
                {dataTableLoading ? (
                    Array.from({ length: 10 }).map((_, index) => (
                        <Skeleton key={index} shape="rectangle" width="100%" height="40px" className="mb-1" />
                    ))
                ) : (
                    <>
                        <div className="d-flex flex-wrap justify-content-between align-items-center">
                            <div className="d-flex align-items-center p-3">
                                <span className="me-2">Show:</span>
                                <InputNumber
                                    value={rowsPerPage}
                                    onChange={(e) => setRowsPerPage(e.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            navigate(`/StockValuation/${rowsPerPage}/${sortOrder}`)
                                        }
                                    }}
                                    mode="decimal"
                                    showButtons
                                    min={1}
                                    step={10}
                                    className="me-3"
                                />
                                <span className="me-2">entries</span>
                            </div>
                            <div className="d-flex align-items-center p-3">
                                <span className="me-2">Sort Order:</span>
                                <select
                                    className="form-select"
                                    value={sortOrder}
                                    onChange={(e) => { setSortOrder(e.target.value); navigate(`/StockValuation/${rowsPerPage}/${e.target.value}`) }}
                                    style={{ width: '100px' }}
                                >
                                    <option value="ASC">Ascending</option>
                                    <option value="DESC">Descending</option>
                                </select>
                            </div>
                        </div>
                        <DataTable
                            value={rows}
                            paginator
                            rows={rowsPerPage}
                            paginatorTemplate="CurrentPageReport PaginatorLeft"
                            currentPageReportTemplate="Total Records: {totalRecords}"
                            paginatorLeft={paginatorLeft}
                            paginatorRight={<></>}
                            sortOrder={sortOrder === 'ASC' ? 1 : -1}
                        >
                            <Column field="product_name" header="Product Name" sortable />
                            <Column field="product_barcode" header="Barcode" />
                            <Column field="total_stock_in" header="Stock In" />
                            <Column field="total_stock_out" header="Stock Out" />
                            <Column field="remaining_stock" header="Remaining Stock" />
                            <Column field="stock_purchase_price" header="Purchase Price" />
                            <Column
                                field="stock_valuation"
                                header="Stock Valuation"
                                body={(rowData) => Number(rowData.stock_valuation).toFixed(2)}
                                footer={() => {
                                    const total = rows.reduce((sum, item) => sum + parseFloat(item.stock_valuation || 0), 0);
                                    return `₹ ${total.toFixed(2)}`;
                                }}
                            />
                        </DataTable>
                    </>
                )}
            </div>
            <div className="mb-5"></div>
        </div>
    );
};

export default StockValuationReport;
